import DatePickerInput from 'components/Common/Form/Input/DatePickerInput'
import FakeInput from 'components/Common/Form/Input/FakeInput'
import TextInput from 'components/Common/Form/Input/TextInput'
import TimeInput from 'components/Common/Form/Input/TimeInput'
import Group from 'components/utils/Group'
import { addDays, convertTZ, startOfDay } from 'lib/datetime/dateUtils'
import noop from 'lib/function/noop'
import { useCallback, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import ExperienceTransferBookingHotelSelect from './ExperienceTransferBookingHotelSelect'
import type { GoogleAutocompletePlaceItem } from 'api/search'
import moment from 'moment'

const StretchedGroup = styled(Group)`
  > * {
    flex: 1;
  }
`

interface Props {
  transfer: App.ExperienceTransferView
  name: string
  disableLocations?: boolean
  onChange?: (transfer: App.ExperienceTransferView) => void
  experience: App.ExperienceOffer
}

function ExperienceTransferBookingForm(props: Props) {
  const {
    name,
    transfer,
    disableLocations,
    experience,
    onChange = noop,
  } = props

  const intl = useIntl()

  // A transfer experience should always have an airport
  const airport = experience.airport!
  const airportWithCode = intl.formatMessage(
    {
      id: 'XTKEi5tYCIXqIQ1X9ntg',
      defaultMessage: '{airportName} ({airportCode})',
    },
    { airportName: airport.name, airportCode: airport.code },
  )

  const minDate = useMemo(() => {
    const cutoffDays = ((experience.bookingCutoffHours ?? 0) / 24) + 1
    return addDays(startOfDay(new Date()), cutoffDays)
  }, [experience.bookingCutoffHours],
  )

  const onHotelChange = useCallback((option: GoogleAutocompletePlaceItem) => {
    onChange({
      ...transfer,
      hotel: {
        name: option.mainText,
        kmFromAirport: option.distanceMeters ? Math.ceil(option.distanceMeters / 1000) : undefined,
        googlePlaceId: option.placeId,
      },
    })
  }, [onChange, transfer])

  const onDateChange = useCallback((date: Date) => {
    // need this value only for pick up
    if (name !== 'toHotel') return
    onChange({
      ...transfer,
      date: date && moment(date).format('YYYY-MM-DD'),
    })
  }, [onChange, transfer, name])

  const expirationDate = useMemo(() => {
    if (experience.expirationDate) {
      const expirationDate = new Date(experience.expirationDate)
      if (!experience.location?.timezone) {
        return expirationDate
      }
      return convertTZ(expirationDate, experience.location.timezone)
    }
  }, [experience.expirationDate, experience.location?.timezone])

  const fromHotelLabel = intl.formatMessage({
    id: '5d7e1PEctcPT_2WMqPb3',
    defaultMessage: 'From (Hotel)',
  })
  const hotelPickUpPlaceholder = intl.formatMessage({
    id: 'jTIe6tvYeBvGl9PpCuZ3',
    defaultMessage: 'Hotel pick up',
  })
  const hotelPickUpError = intl.formatMessage({
    id: 'wXWv6HP2S41i6a7jr7-z',
    defaultMessage: 'Please specify your hotel pick up',
  })
  const toAirportLabel = intl.formatMessage({
    id: 'UkKCqRSv-iy3RNbN4xrU',
    defaultMessage: 'To (Airport)',
  })
  const fromAirportLabel = intl.formatMessage({
    id: '7OQM-9BduXSwFevTAFVn',
    defaultMessage: 'From (Airport)',
  })
  const toHotelLabel = intl.formatMessage({
    id: '2wp2Z8EihT8dOo5_-8pM',
    defaultMessage: 'To (Hotel)',
  })
  const hotelDropOffPlaceholder = intl.formatMessage({
    id: 'F0ySDZFCeYkPlu1I5_lz',
    defaultMessage: 'Hotel drop off',
  })
  const hotelDropOffError = intl.formatMessage({
    id: 'KZlOB2x3Q-Ybl6aEbM6u',
    defaultMessage: 'Please specify your hotel drop off',
  })
  const flightNumberLabel = intl.formatMessage({
    id: '8b7hg6rupiPEJfvdMbT0',
    defaultMessage: 'Flight number',
  })
  const flightNumberPlaceholder = intl.formatMessage({
    id: 'Hnogxm7r9LyJHHRlwFLy',
    defaultMessage: 'e.g. QF123',
  })
  const arrivalDateLabel = intl.formatMessage({
    id: 'Va6og6uMC6bTIWkj7ICR',
    defaultMessage: 'Arrival date',
  })
  const departureDateLabel = intl.formatMessage({
    id: 'RByemg1_cI-1n6hOfsKu',
    defaultMessage: 'Departure date',
  })
  const arrivalTimeLabel = intl.formatMessage({
    id: 'becB19Jp4gwaNdO15o6N',
    defaultMessage: 'Arrival time',
  })
  const departureTimeLabel = intl.formatMessage({
    id: 'm_KXunn3sdbqOwWTjCBd',
    defaultMessage: 'Departure time',
  })
  const arrivalDateError = intl.formatMessage({
    id: 'OFNVABC9ZvDCdvxAiCla',
    defaultMessage: 'Please provide date of arrival',
  })
  const departureDateError = intl.formatMessage({
    id: 'dRxamBraouxEkelIAmS2',
    defaultMessage: 'Please provide date of departure',
  })
  const arrivalTimeError = intl.formatMessage({
    id: 'gyeylEJ0fygGMwIW7y2l',
    defaultMessage: 'Please provide time of arrival',
  })
  const departureTimeError = intl.formatMessage({
    id: '83-EIM_O2LDk4PiR1WbZ',
    defaultMessage: 'Please provide time of departure',
  })
  const selectTimePlaceholder = intl.formatMessage({
    id: 'NM1GYLaycxltHO9XRSmP',
    defaultMessage: 'Select time',
  })

  return <>
    <StretchedGroup tabletDirection="horizontal" direction="vertical" gap={12}>
      <input type="hidden" name={`${name}.type`} value={transfer.type} />
      {transfer.type === 'HOTEL-TO-AIRPORT' && <>
        <ExperienceTransferBookingHotelSelect
          label={fromHotelLabel}
          required
          name={`${name}.hotel`}
          placeholder={hotelPickUpPlaceholder}
          disabled={disableLocations}
          transfer={transfer}
          onChange={onHotelChange}
          requiredErrorMessage={hotelPickUpError}
          airport={airport}
        />
        <FakeInput
          label={toAirportLabel}
          value={airportWithCode}
          required
          disabled
        />
      </>}
      {transfer.type == 'AIRPORT-TO-HOTEL' && <>
        <FakeInput
          label={fromAirportLabel}
          value={airportWithCode}
          required
          disabled
        />
        <ExperienceTransferBookingHotelSelect
          label={toHotelLabel}
          required
          name={`${name}.hotel`}
          placeholder={hotelDropOffPlaceholder}
          transfer={transfer}
          disabled={disableLocations}
          onChange={onHotelChange}
          airport={airport}
          requiredErrorMessage={hotelDropOffError}
        />
      </>}
    </StretchedGroup>
    <StretchedGroup tabletDirection="horizontal" direction="vertical" gap={12}>
      <TextInput
        label={flightNumberLabel}
        defaultValue={transfer.flightNumber}
        name={`${name}.flightNumber`}
        placeholder={flightNumberPlaceholder}
      />
      <DatePickerInput
        label={transfer.type === 'AIRPORT-TO-HOTEL' ? arrivalDateLabel : departureDateLabel}
        required
        min={minDate}
        max={expirationDate}
        defaultValue={transfer.date}
        name={`${name}.date`}
        requiredErrorMessage={transfer.type === 'AIRPORT-TO-HOTEL' ? arrivalDateError : departureDateError}
        onDateChange={onDateChange}
      />
      <TimeInput
        label={transfer.type === 'AIRPORT-TO-HOTEL' ? arrivalTimeLabel : departureTimeLabel}
        defaultValue={transfer.time}
        name={`${name}.time`}
        placeholder={selectTimePlaceholder}
        step={15}
        requiredErrorMessage={transfer.type === 'AIRPORT-TO-HOTEL' ? arrivalTimeError : departureTimeError}
      />
    </StretchedGroup>
  </>
}

export default ExperienceTransferBookingForm
